
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import Player from '@/components/Player.vue';
import Powerlink from '@/components/Powerlink.vue';
import StartTemplate from '@/components/templates/StartTemplate.vue'; // @ is an alias to /src
import ErrorTemplate from '@/components/templates/ErrorTemplate.vue';
import StopLiveTemplate from '@/components/templates/StopLiveTemplate.vue'; // @ is an alias to /src
import ErrorPlayerTemplate from '@/components/templates/ErrorPlayerTemplate.vue'; // @ is an alias to /src
import LoadingTemplate from '@/components/templates/LoadingTemplate.vue'; // Loading screen

import { etvService } from './services/etv.service';
import { configService } from './services/config.service';
import { statisticService } from './services/statistic.service';
import { logService, LogLevel } from './services/log.service';
import { iFrameService } from './services/iframe.service';
import { schemaService } from './services/schema.service';
import { versionService } from './services/version.service';

import { PlayerConfig, EtvConfig } from '@/interfaces/player-config.interface';
const packageVersion = require('../package.json').version;
const gitVersion = versionService.getVersion();

export enum Template {
  START = 'START',
  ERROR = 'ERROR',
  STOP = 'STOP',
  PLAYER_ERROR = 'PLAYER_ERROR',
  PLAYER = 'PLAYER',
  LOADING = 'LOADING',
  NOTHING = 'NOTHING',
}

@Component({
  inheritAttrs: true,
  components: {
    StartTemplate,
    ErrorTemplate,
    StopLiveTemplate,
    ErrorPlayerTemplate,
    LoadingTemplate,
    Player,
    Powerlink,
  },
})
export default class App extends Vue {
  // Make Template enum available in template
  public Template = Template;

  @Prop() public token!: string;
  @Prop() public loglevel?: LogLevel;
  @Prop() public width?: string;
  @Prop() public ratio?: string;
  @Prop() public id?: string;
  @Prop() public autoplay?: boolean;
  @Prop() public mute?: boolean;
  @Prop() public adTargetTag?: string;

  // Clip/query parameters (props use lowercase names for consistency between iframe and Light DOM)
  @Prop() public query?: string;
  @Prop() public limit?: number;
  @Prop() public offset?: number;
  @Prop() public sortfield?: string;
  @Prop() public sortorder?: string;
  @Prop() public maxduration?: number;

  public template: Template = Template.NOTHING;
  public account: string = '';
  public isPlayerFadingIn: boolean = false;

  public playerConfig: PlayerConfig | null = null;
  public etvConfig: EtvConfig | null = null;

  private ready: boolean = false;
  private lastConfigHash: string = '';

  private parentHost = '';
  private parentFullURl = '';

  get includePowerlink(): boolean {
    // Check build-time environment variable
    // iframe build: VUE_APP_EXCLUDE_POWERLINK=true (powerlink created by etvp.js on parent)
    // Light DOM build: VUE_APP_EXCLUDE_POWERLINK=false (powerlink needed in player)
    return process.env.VUE_APP_EXCLUDE_POWERLINK !== 'true';
  }

  get aspectRatio() {
    // return default 16:9
    if (!this.ratio) return (9 / 16) * 100 + '%';

    const [w, h] = this.ratio.split(':').map((x) => parseInt(x));

    // return default 16:9
    if (isNaN(w) || isNaN(h)) return (9 / 16) * 100 + '%';

    return (h / w) * 100 + '%';
  }

  // Watch for token changes and reinitialize
  @Watch('token')
  private onTokenChanged(newToken: string, oldToken: string) {
    if (oldToken && newToken && newToken !== oldToken) {
      logService.debug('[APP] Token changed, reinitializing player:', { old: oldToken, new: newToken });
      this.reinitializePlayer(newToken);
    }
  }

  private reinitializePlayer(token: string) {
    // Reset player state
    this.template = Template.NOTHING;
    this.account = '';
    this.ready = false;

    // Reinitialize with new token
    if (token) {
      this.getETVConfig(token);
    } else {
      logService.warn('No token provided for reinitialization');
      this.setTemplate(Template.ERROR);
    }
  }

  private beforeCreate() {
    // In web components, props are not available in beforeCreate
    // Initialize with default values, we'll re-initialize in created() with proper props
    const logLevel: LogLevel = (process.env.VUE_APP_LOG_LEVEL as LogLevel) ?? LogLevel.INFO;
    logService.init(logLevel, 'etv-player-temp');

    logService.trace('[APP] beforeCreate - Initialized with defaults');
  }

  private async created() {
    // Now props are available, re-initialize logService with correct values
    const logLevel: LogLevel = this.loglevel ?? (process.env.VUE_APP_LOG_LEVEL as LogLevel) ?? LogLevel.INFO;
    logService.init(logLevel, this.id);

    // Log version info now that we have proper props
    if (process.env.NODE_ENV === 'production') {
      logService.info(
        `\n\n------ ETV PLAYER VERSION: ${gitVersion} (pkg: ${packageVersion}) ------\n` +
          `• ID: ${this.id ?? 'etv-player'}\n` +
          `• MODE: ${process.env.NODE_ENV}\n` +
          `• LOG-LEVEL: ${logLevel}\n` +
          `---------------------------------------\n\n`
      );
    } else {
      logService.info(
        `\n\n------ ETV PLAYER VERSION: ${gitVersion} (pkg: ${packageVersion}) ------\n` +
          `• ID: ${this.id ?? 'etv-player'}\n` +
          `• MODE: ${process.env.NODE_ENV}\n` +
          `• API PATH: ${process.env.VUE_APP_API_PATH}\n` +
          `• LOG-LEVEL: ${logLevel}\n` +
          `• REPORTING ENABLED: ${process.env.VUE_APP_REPORTING_ENABLED}\n` +
          `• REPORTING PATH: ${process.env.VUE_APP_REPORTING_PATH}\n` +
          `---------------------------------------\n\n`
      );
    }

    logService.trace('[APP] created - Props available:', {
      token: this.token,
      loglevel: this.loglevel,
      id: this.id,
      autoplay: this.autoplay,
      adTargetTag: this.adTargetTag,
      width: this.width,
      ratio: this.ratio
    });

    try {
      if (this.inIframe()) {
        const parentUrl = await iFrameService.handshake();
        const url = new URL(parentUrl);
        this.parentHost = url.host ? url.host : parentUrl;
        this.parentFullURl = parentUrl;

        // Setup consent listener after handshake
        iFrameService.setupConsentListener((consent) => {
          logService.debug('[APP] Received consent from parent:', consent);
          this.updateConsentMode(consent);
        });

        // Request current consent status from parent
        iFrameService.requestConsent();
      } else {
        this.parentHost = location.host;
        this.parentFullURl = location.origin + location.pathname;
      }

      // Check for consent in URL parameters as fallback
      // Format: ?consent=granted or ?consent=denied
      // Or detailed: ?ad_storage=granted&analytics_storage=granted
      this.checkConsentFromURL();

    } catch (error) {
      this.parentHost = location.host;
      this.parentFullURl = location.origin + location.pathname;
    }

    if (this.token) {
      this.getETVConfig(this.token);
    } else {
      logService.warn('Query not valid');
      this.setTemplate(Template.ERROR);
    }
  }

  private mounted() {
    // Clean mounted hook - attribute conversion is working via kebab-case
    logService.trace('[APP] mounted - Props after mount:', {
      autoplay: this.autoplay,
      adTargetTag: this.adTargetTag
    });

    // Light DOM web component - no special structure creation needed
    // Vue template renders directly to Light DOM via our custom element

    // Expose version API globally
    if (typeof window !== 'undefined') {
      (window as any).ETVPlayer = (window as any).ETVPlayer || {};
      (window as any).ETVPlayer.version = gitVersion;
      (window as any).ETVPlayer.getVersion = () => versionService.getVersionInfo();
    }
  }

  private inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  /**
   * Update Google Consent Mode based on consent from parent page
   */
  private updateConsentMode(consent: any) {
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('consent', 'update', {
        ad_storage: consent.ad_storage || 'denied',
        analytics_storage: consent.analytics_storage || 'denied',
        ad_user_data: consent.ad_user_data || 'denied',
        ad_personalization: consent.ad_personalization || 'denied',
      });
      logService.info('[APP] Consent mode updated:', consent);
    } else {
      logService.warn('[APP] gtag not available for consent update');
    }
  }

  private getETVConfig(token: any) {
    etvService
      .init(token)
      .then(async (configRef: any) => {
        this.subscribeToETV(configRef);
      })
      .catch((e: Error) => {
        logService.error('getETVConfig', e);
        this.setTemplate(Template.ERROR);
      });
  }

  private async subscribeToETV(configRef: any) {
    configRef.subscribe(async (etvConfig: EtvConfig) => {
      try {
        // Smart Config Reload: Skip update if config hasn't changed
        const configHash = JSON.stringify(etvConfig);
        if (configHash === this.lastConfigHash) {
          logService.debug('[APP] Config unchanged, skipping update');
          return;
        }
        this.lastConfigHash = configHash;
        logService.debug('[APP] Config changed, processing update');

        this.etvConfig = etvConfig; // Store the full ETV config

        // FORCE attribute reading for web components - they don't always bind props correctly
        let adTargetTag = this.adTargetTag;
        let autoplay = this.autoplay;

        // In Light DOM web component, props should work correctly
        // but we can still fall back to DOM attributes if needed
        if (!adTargetTag) {
          const etvPlayer = document.querySelector('etv-player');
          if (etvPlayer) {
            const attrValue = etvPlayer.getAttribute('ad-target-tag') ||
                             etvPlayer.getAttribute('adTargetTag') ||
                             etvPlayer.getAttribute('adtargettag');
            if (attrValue) {
              adTargetTag = attrValue;
              logService.trace('[APP] Found adTargetTag from DOM attribute:', attrValue);
            }
          }
        }

        if (autoplay === undefined) {
          const etvPlayer = document.querySelector('etv-player');
          if (etvPlayer) {
            const autoplayAttr = etvPlayer.getAttribute('autoplay');
            if (autoplayAttr !== null) {
              autoplay = autoplayAttr === 'true' || autoplayAttr === '' || autoplayAttr === 'autoplay';
              logService.trace('[APP] Found autoplay from DOM attribute:', { attr: autoplayAttr, value: autoplay });
            }
          }
        }

        const userConfig = {
          token: this.token,
          autoplay: autoplay,
          mute: this.mute,
          adTargetTag: adTargetTag,
          // Playlist query parameters (lowercase props map to config.service expectations)
          query: this.query,
          sortField: this.sortfield,     // map to sortField for config.service
          sortOrder: this.sortorder,     // map to sortOrder for config.service
          limit: this.limit,
          offset: this.offset,
          max_duration: this.maxduration // map to max_duration for config.service
        };
        logService.debug('[APP] userConfig being passed to configService:', userConfig);
        this.playerConfig = await configService.getConfig(this.parentHost, this.parentFullURl, userConfig, etvConfig);

        // Debug advertisement configuration
        logService.debug('[APP] EtvConfig advertisement:', etvConfig.template.advertisement);
        logService.debug('[APP] PlayerConfig advertisement:', this.playerConfig.advertisement);

        logService.trace('[APP] userConfig update:', { token: this.token });
        logService.trace('[APP] etvConfig update:', this.playerConfig);
        this.account = 'acc-' + etvConfig.template.accountId;

        // Only run initialization once, not on every config update
        if (!this.ready) {
          await this.setConfig(etvConfig);
          await this.handleAdvertisementInitialization();
        }

        // Always update config in services (lightweight operation)
        this.updateConfig(etvConfig);
        schemaService.setSchema(etvConfig, this.playerConfig);

        if (this.playerConfig && this.playerConfig.maintenance) {
          logService.warn('maintenance mode is active');
          this.setTemplate(Template.ERROR);
        } else if (this.template === Template.NOTHING || this.template === Template.ERROR) {
          (this.playerConfig && this.playerConfig.autoplay) ? this.setTemplate(Template.PLAYER) : this.setTemplate(Template.START);
        }
      } catch (error) {
        this.setTemplate(Template.ERROR);
        console.error(error);
      }
    });
  }

  private async setConfig(etvConfig: EtvConfig) {
    logService.debug('[APP] setConfig called, ready flag:', this.ready);

    if (this.ready) {
      logService.debug('[APP] setConfig skipped - already ready');
      return;
    }

    try {
      // Initialize analytics first if available
      if (etvConfig.template.analytics.google) {
        await statisticService.initGTMScript(etvConfig.template.analytics.google, etvConfig, this.playerConfig!);
      }

      // Initialize reporting if enabled
      if (process.env.VUE_APP_REPORTING_ENABLED !== 'false') {
        await statisticService.initReporting();
      }

      // Mark as ready only after all async operations complete
      this.ready = true;
    } catch (error) {
      logService.error('[APP] Error during setConfig:', error);
      throw error;
    }

  iFrameService.initParent(this.playerConfig!);

    statisticService.ga('Info');
  }

  private async handleAdvertisementInitialization() {
  logService.debug('[APP] handleAdvertisementInitialization called');
  logService.debug('[APP] Checking advertisement.enabled:', this.playerConfig!.advertisement.enabled);

  if (this.playerConfig!.advertisement.enabled) {
      // Check if IMA SDK is already loaded
      if (typeof (window as any).google !== 'undefined' && (window as any).google.ima) {
        logService.debug('[APP] Google IMA SDK already available');
        return;
      }

      try {
        logService.debug('[APP] Loading Google IMA SDK...');
        await this.loadScriptNative('https://imasdk.googleapis.com/js/sdkloader/ima3.js');

        // Verify the SDK loaded correctly
        if (typeof (window as any).google === 'undefined' || !(window as any).google.ima) {
          logService.error('[APP] Google IMA SDK script loaded but google.ima not available');
          this.playerConfig!.advertisement.enabled = false;
          return;
        }

        logService.debug('[APP] Google IMA SDK loaded successfully');
        logService.debug('[APP] IMA SDK version:', (window as any).google.ima.VERSION);
      } catch (error) {
        logService.error('[APP] Failed to load Google IMA SDK:', error);
        logService.error('[APP] Error details:', (error as any)?.message || error);
  this.playerConfig!.advertisement.enabled = false;
      }
    } else {
      logService.debug('[APP] Advertisement disabled, skipping IMA SDK loading');
    }
  }

  private loadScriptNative(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        logService.trace('[APP] Script already exists in DOM');
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;

      script.onload = () => {
        logService.trace('[APP] Script loaded successfully');
        resolve();
      };

      script.onerror = (error) => {
        logService.error('[APP] Script failed to load:', error);
        reject(new Error(`Failed to load script: ${src}`));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * update config in all relevant services
   */
  private updateConfig(etvConfig: EtvConfig) {
    statisticService.updateConfig(etvConfig);
  }

  public setTemplate(template: Template) {
    logService.info('setTemplate: ' + template);

    // CRITICAL FIX: Always set isPlayerFadingIn = true when switching to PLAYER template
    // This ensures the opacity transitions properly from 0 -> 1
    // Previously, isPlayerFadingIn was only set in onPlayerAdsReady(), causing opacity
    // to stay at 0 when switching from ad error or other states to PLAYER
    if (template === Template.PLAYER) {
      this.isPlayerFadingIn = true;
      logService.debug('[APP] Set isPlayerFadingIn = true for PLAYER template transition');
    } else {
      // Reset fade-in flag when switching away from PLAYER for clean state management
      this.isPlayerFadingIn = false;
    }

    this.template = template;
    // Vue's reactivity will handle re-rendering the template automatically
  }

  /**
   * Called when Player component emits playerReady event (when ads are disabled)
   * This means the player is ready to play content without ads
   */
  public onPlayerReadyWithoutAds() {
    logService.debug('[APP] playerReady event received from Player (no ads) - triggering play');

    // Wait a tick for Vue to finish rendering
    this.$nextTick(() => {
      const playerComponent = this.$refs.player as any;
      if (playerComponent && playerComponent.play) {
        logService.debug('[APP] Calling player.play() to start content (no ads)');
        playerComponent.play();
      } else {
        logService.warn('[APP] Player component or play() method not available');
      }
    });
  }

  /**
   * Check for consent values in URL parameters as fallback
   * Supports two formats:
   * 1. Simple: ?consent=granted or ?consent=denied
   * 2. Detailed: ?ad_storage=granted&analytics_storage=granted&ad_user_data=granted&ad_personalization=granted
   */
  private checkConsentFromURL() {
    try {
      const urlParams = new URLSearchParams(window.location.search);

      // Check for simple consent parameter
      const simpleConsent = urlParams.get('consent');
      if (simpleConsent === 'granted' || simpleConsent === 'denied') {
        logService.debug('[APP] Found simple consent in URL:', simpleConsent);
        this.updateConsentMode({
          ad_storage: simpleConsent,
          analytics_storage: simpleConsent,
          ad_user_data: simpleConsent,
          ad_personalization: simpleConsent,
        });
        return;
      }

      // Check for detailed consent parameters
      const adStorage = urlParams.get('ad_storage');
      const analyticsStorage = urlParams.get('analytics_storage');
      const adUserData = urlParams.get('ad_user_data');
      const adPersonalization = urlParams.get('ad_personalization');

      if (adStorage || analyticsStorage || adUserData || adPersonalization) {
        logService.debug('[APP] Found detailed consent parameters in URL');
        this.updateConsentMode({
          ad_storage: adStorage || 'denied',
          analytics_storage: analyticsStorage || 'denied',
          ad_user_data: adUserData || 'denied',
          ad_personalization: adPersonalization || 'denied',
        });
      } else {
        logService.debug('[APP] No consent parameters found in URL');
      }
    } catch (error) {
      logService.error('[APP] Error checking consent from URL:', error);
    }
  }


  /**
   * Called when Player component emits adsReady event
   * This means IMA has fired 'adsready' and initializeAdDisplayContainer() was called
   * Now we can safely trigger play() to start the preroll ad
   */
  public onPlayerAdsReady() {
    logService.debug('[APP] adsReady event received from Player - triggering auto-play');

    // If we're in LOADING state, switch to PLAYER now (player is ready!)
    if (this.template === Template.LOADING) {
      logService.debug('[APP] Switching from LOADING to PLAYER (player ready)');

      // Start fade-in animation for player
      this.isPlayerFadingIn = true;

      // Fade out LoadingTemplate and switch template
      const loadingTemplate = this.$refs.loadingTemplate as any;
      if (loadingTemplate && loadingTemplate.fadeOut) {
        loadingTemplate.fadeOut();
      }

      // Switch template after a brief moment to allow LoadingTemplate fade-out to start
      setTimeout(() => {
        this.setTemplate(Template.PLAYER);
      }, 100);
    }

    // Wait a tick for Vue to finish rendering
    this.$nextTick(() => {
      const playerComponent = this.$refs.player as any;
      if (playerComponent && playerComponent.play) {
        logService.debug('[APP] Calling player.play() to start preroll');
        playerComponent.play();
      } else {
        logService.warn('[APP] Player component or play() method not available');
      }
    });
  }

  public async reloadPlayer() {
    etvService
      .reload()
      .then(() => {
        if (this.playerConfig) {
          this.playerConfig.autoplay = true;
          setTimeout(() => {
            this.setTemplate(Template.PLAYER);
          }, 10);
        }
      })
      .catch(() => {
        this.setTemplate(Template.ERROR);
      });
  }
}
